import {
  AuthenticationService,
  ProtectedRouteWithAudience,
  TwinAuthConfiguration
} from '@shared/authentication';
import { EnvironmentProviders, inject, Provider, provideAppInitializer } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { environment } from '@environment/environment';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import Keycloak from 'keycloak-js';

export class BoostrapAuthConfigHelper {
  static getProtectedUrls(): ProtectedRouteWithAudience[] {
    if (environment.offline) {
      return [];
    }

    const urls: ProtectedRouteWithAudience[] = [
      {
        route: environment.api,
        audience: environment.auth0.audience
      }
    ];

    if (environment.ioData?.url) {
      urls.push({
        route: environment.ioData.url,
        audience: environment.auth0.audience
      });
    }

    if (Array.isArray(environment.mediaServers)) {
      const mediaRoutes = environment.mediaServers
        .filter(url => url !== '')
        .map(url => ({
          route: url,
          audience: environment.auth0.audience
        }));
      urls.push(...mediaRoutes);
    }

    return urls;
  }

  static getTwinAuthOptions(): TwinAuthConfiguration {
    return {
      tenantId: environment.auth0.tenantId,
      clientId: environment.auth0.clientId,
      domain: environment.auth0.domain,
      audience: environment.auth0.audience,
      scope: environment.auth0.scope,
      redirectUri: `${window.location.origin}${environment.auth0.redirect}`,
      protectedRoutes: this.getProtectedUrls(),
      autologin: true,
      afterLoginRedirect: 'origin',
      errorRedirects: {
        unauthorizedUrl: '/invalid'
      }
    };
  }

  static getOfflineAuthOptions(): TwinAuthConfiguration {
    return {
      clientId: environment.keycloak?.clientId ?? 'ENV keycloak.clientId IS NOT SET',
      domain: environment.keycloak?.url ?? 'ENV keycloak.url IS NOT SET',
      audience: environment.keycloak?.realm ?? 'ENV keycloak.realm IS NOT SET',
      scope: '',
      redirectUri: `${window.location.origin}${environment.auth0.redirect}`,
      protectedRoutes: this.getProtectedUrls(),
      autologin: false,
      afterLoginRedirect: 'origin',
      errorRedirects: {
        unauthorizedUrl: '/invalid'
      }
    };
  }

  static keycloakInitializer(keycloak: Keycloak): () => Promise<boolean> {
    return () => {
      if (environment.offline) {
        return keycloak.init({
          onLoad: 'login-required',
          silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`
        });
      }
      return Promise.resolve(true);
    };
  }

  static getKeycloakInitializerProvider(): EnvironmentProviders {
    return provideAppInitializer(() => this.keycloakInitializer(inject(Keycloak))());
  }

  static getApolloProvider(): Provider {
    return provideNamedApollo(() => {
      const authService = inject(AuthenticationService);
      const httpLink = inject(HttpLink);

      const tokenSignal = toSignal(authService.accessToken$, { initialValue: null });

      const authLink = new SetContextLink(async prevContext => {
        const token = tokenSignal();

        return {
          ...prevContext,
          headers: {
            ...prevContext?.headers,
            Authorization: token ? `Bearer ${token}` : ''
          }
        };
      });

      return {
        coreGraphQL: {
          link: authLink.concat(httpLink.create({ uri: environment.graphql.coreApi })),
          cache: new InMemoryCache()
        },
        linearGraphQL: {
          link: authLink.concat(httpLink.create({ uri: environment.graphql.linearApi })),
          cache: new InMemoryCache()
        }
      };
    });
  }

  static getAgGridLicenceKey(): string {
    return 'Using_this_{AG_Grid}_Enterprise_key_{AG-083831}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Elementz_Limited}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Integrity_Elements}_only_for_{4}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Integrity_Elements}_need_to_be_licensed___{Integrity_Elements}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{4_October_2026}____[v3]_[01]_MTc5MTA2ODQwMDAwMA==ff92589eb4161e468e90656446e172ba';
  }
}
